const DEV_NEW_VIEW_URL = "http://localhost:3003/";
const PROD_NEW_VIEW_URL = "/beta/";

/**
 * Query param carried by manual view-switch links (e.g. NavigationHeader's "new view" link).
 * Its presence tells the destination app's BetaOptInGuard that this visit is an intentional,
 * one-off navigation, not the user's default landing — so the guard should not bounce them
 * back based on their stored opt-in preference.
 */
export const MANUAL_VIEW_PARAM = "manualView";

/**
 * URL of the new (V2) app to link to from the "New View" entry point.
 * In dev this is cross-port (V1's dev server only serves itself); in production
 * builds it's same-origin — V1 is the default app at / during the beta opt-in
 * rollout (PFS-10283), with V2 reachable under /beta/. Revert PROD_NEW_VIEW_URL to
 * "/" (and the matching nginx/Dockerfile routing) once the rollout is complete and
 * V2 becomes the permanent default again.
 */
export function getNewViewUrl(nodeEnv: string | undefined = process.env.NODE_ENV): string {
	return nodeEnv === "production" ? PROD_NEW_VIEW_URL : DEV_NEW_VIEW_URL;
}

const DEV_NEW_VIEW_V3_URL = "http://localhost:3004/";
const PROD_NEW_VIEW_V3_URL = "/v3/";

/**
 * URL of the V3 app (app-v3) to link to from the "New view v3" entry point. Same split as
 * getNewViewUrl: cross-port in dev (app-v3's dev server runs on 3004), same-origin in
 * production, where nginx serves V3 under /v3/.
 */
export function getNewViewV3Url(nodeEnv: string | undefined = process.env.NODE_ENV): string {
	return nodeEnv === "production" ? PROD_NEW_VIEW_V3_URL : DEV_NEW_VIEW_V3_URL;
}

/**
 * Premium launch params. takeoffs-backend's /auth/simpro handler redirects a launch from a
 * Premium quote or job to `/?q=<quoteId>` or `/?j=<jobId>`, and MainRouter turns those into
 * the project page. When BetaOptInGuard sends an opted-in user to the new view, these must
 * travel with them or they land on V2's project list instead of the project they opened.
 */
const LAUNCH_PARAMS = ["q", "j"] as const;

/**
 * New-view URL carrying the Premium launch params found in `search` (if any). Everything
 * else in the query (Pendo's encodedUri/e, the manualView flag) is deliberately dropped.
 */
export function getNewViewLaunchUrl(search: string, nodeEnv: string | undefined = process.env.NODE_ENV): string {
	const source = new URLSearchParams(search);
	const launchParams = new URLSearchParams();
	LAUNCH_PARAMS.forEach(name => {
		const value = source.get(name);
		if (value) {
			launchParams.set(name, value);
		}
	});
	const baseUrl = getNewViewUrl(nodeEnv);
	const query = launchParams.toString();
	// Safe to append with a bare "?": both DEV_NEW_VIEW_URL and PROD_NEW_VIEW_URL are plain
	// paths with no query string of their own.
	return query ? `${baseUrl}?${query}` : baseUrl;
}
